import React, { useEffect, useMemo, useState } from "react";
import { useTranslate, useOne, useUpdate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useSelect } from "@refinedev/antd";
import {
  Form,
  Input,
  Row,
  Col,
  Select,
  Switch,
  InputNumber,
  Alert,
  Button,
  Modal,
  Spin,
  Divider,
  Typography,
  theme,
  Space,
} from "antd";
import {
  HomeOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  CreditCardOutlined,
  KeyOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useSelectValues } from "../values";
import { AccountType } from "../../../models/account";
import { useRequiresQontoInternationalPhone } from "../hooks/useRequiresQontoInternationalPhone";
import { AccountPhoneFormItem } from "./AccountPhoneFormItem";
import { getCountryOptions } from "../../../utils/countries";

const { Title } = Typography;

type AccountEditModalProps = {
  open: boolean;
  accountId: number | string | null;
  onCancel: () => void;
  onSuccess?: () => void;
};

export const AccountEditModal: React.FC<AccountEditModalProps> = ({
  open,
  accountId,
  onCancel,
  onSuccess,
}) => {
  const translate = useTranslate();
  const { i18n } = useTranslation();
  const countryOptions = useMemo(
    () => getCountryOptions(i18n.language),
    [i18n.language],
  );
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const { accountTypes, billingModes } = useSelectValues();
  const [billingMode, setBillingMode] = useState<string | undefined>();
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const bFlashApiKey = Form.useWatch("bFlashApiKey", form);
  const accountType = Form.useWatch("type", form);

  const { data: queryResult, isLoading } = useOne({
    resource: "accounts",
    id: accountId ?? "",
    queryOptions: {
      enabled: open && !!accountId,
    },
  });

  const account = queryResult?.data;
  const requiresInternationalPhone = useRequiresQontoInternationalPhone(
    account
      ? {
          qontoClientId: account.qontoClientId as string | null | undefined,
          qontoSyncError: account.qontoSyncError as boolean | undefined,
        }
      : null,
    accountType,
  );

  const { selectProps: pricingPlanSelectProps } = useSelect({
    resource: "pricing-plans",
    optionLabel: "name",
    optionValue: "id",
    filters: [
      {
        field: "isActive",
        operator: "eq",
        value: true,
      },
    ],
  });

  const { mutate: update, isLoading: isUpdating } = useUpdate();

  useEffect(() => {
    if (account && open) {
      form.setFieldsValue({
        id: account.id,
        name: account.name,
        type: account.type,
        firstName: account.firstName,
        lastName: account.lastName,
        legalId: account.legalId,
        address: account.address,
        zipCode: account.zipCode,
        city: account.city,
        country: account.country,
        phone: account.phone,
        email: account.email,
        billingMode: account.billingMode,
        pricingPlanId: account.pricingPlan?.id,
        credit: account.credit,
        status: account.status,
        applyVat: account.applyVat,
        customVatRate: account.customVatRate,
        bFlashApiKey: account.bFlashApiKey,
      });
      setBillingMode(account.billingMode);
    }
    if (!open) {
      form.resetFields();
      setBillingMode(undefined);
      setIsApiKeyModalOpen(false);
    }
  }, [account, open, form]);

  const handleFinish = (values: any) => {
    if (!accountId) return;

    update(
      {
        resource: "accounts",
        id: accountId,
        values,
      },
      {
        onSuccess: () => {
          form.resetFields();
          onCancel();
          onSuccess?.();
        },
      },
    );
  };

  return (
    <>
      <Modal
        title={translate("accounts.titles.edit")}
        open={open}
        onCancel={onCancel}
        width={1000}
        destroyOnHidden
        footer={
          <Space>
            <Button onClick={onCancel}>
              {translate("buttons.cancel")}
            </Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={isUpdating}
              onClick={() => form.submit()}
            >
              {translate("buttons.save")}
            </Button>
          </Space>
        }
      >
        <Spin spinning={isLoading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            onValuesChange={() => form.validateFields(["credit"])}
            className="!mt-6"
          >
            <Form.Item name={["id"]} hidden rules={[{ required: true }]}>
              <Input readOnly disabled />
            </Form.Item>

            {account?.qontoSyncError && (
              <Alert
                type="error"
                showIcon
                className="!mb-4"
                message={translate("accounts.qontoSyncError")}
                description={account.qontoSyncErrorMessage}
              />
            )}

            {/* Company Info */}
            <Title level={5} className="!mb-4 !mt-2">
              <HomeOutlined className="mr-2" style={{ color: token.colorPrimary }} />
              {translate("accounts.sections.companyInfo")}
            </Title>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={8}>
                <Form.Item
                  label={translate("accounts.fields.type")}
                  name={["type"]}
                  rules={[{ required: true }]}
                >
                  <Select options={accountTypes} />
                </Form.Item>
              </Col>
              {accountType === AccountType.company ? (
                <>
                  <Col xs={24} sm={10}>
                    <Form.Item
                      label={translate("accounts.fields.name")}
                      name={["name"]}
                      rules={[{ required: true }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={6}>
                    <Form.Item
                      label={translate("accounts.fields.legalId")}
                      name={["legalId"]}
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "accounts.validation.legalIdRequiredForCompany",
                          ),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </>
              ) : (
                <>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={translate("accounts.fields.firstName")}
                      name={["firstName"]}
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "accounts.validation.firstNameRequiredForIndividual",
                          ),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item
                      label={translate("accounts.fields.lastName")}
                      name={["lastName"]}
                      rules={[
                        {
                          required: true,
                          message: translate(
                            "accounts.validation.lastNameRequiredForIndividual",
                          ),
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </>
              )}
            </Row>

            <Divider className="!my-2" />

            {/* Address */}
            <Title level={5} className="!mb-4 !mt-4">
              <EnvironmentOutlined className="mr-2" style={{ color: token.colorPrimary }} />
              {translate("accounts.sections.address")}
            </Title>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={translate("accounts.fields.address")}
                  name={["address"]}
                  rules={[
                    {
                      required: true,
                      message: translate("accounts.validation.addressRequired"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Item
                  label={translate("accounts.fields.zipCode")}
                  name={["zipCode"]}
                  rules={[
                    {
                      required: true,
                      message: translate("accounts.validation.zipCodeRequired"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Item
                  label={translate("accounts.fields.city")}
                  name={["city"]}
                  rules={[
                    {
                      required: true,
                      message: translate("accounts.validation.cityRequired"),
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={12} sm={4}>
                <Form.Item
                  label={translate("accounts.fields.country")}
                  name={["country"]}
                  rules={[
                    {
                      required: true,
                      message: translate("accounts.validation.countryRequired"),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    options={countryOptions}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider className="!my-2" />

            {/* Contact */}
            <Title level={5} className="!mb-4 !mt-4">
              <PhoneOutlined className="mr-2" style={{ color: token.colorPrimary }} />
              {translate("accounts.sections.contact")}
            </Title>
            <Row gutter={[16, 0]}>
              <Col xs={24} sm={12}>
                <AccountPhoneFormItem
                  label={translate("accounts.fields.phone")}
                  requiresInternationalFormat={requiresInternationalPhone}
                  translate={translate}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  label={translate("accounts.fields.email")}
                  name={["email"]}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Divider className="!my-2" />

            {/* Billing */}
            <Title level={5} className="!mb-4 !mt-4">
              <CreditCardOutlined className="mr-2" style={{ color: token.colorPrimary }} />
              {translate("accounts.sections.billing")}
            </Title>
            <Row gutter={[16, 0]}>
              <Col xs={12} sm={6}>
                <Form.Item
                  label={translate("accounts.fields.billingMode")}
                  name={["billingMode"]}
                  rules={[{ required: true }]}
                >
                  <Select
                    options={billingModes}
                    onChange={(val) => setBillingMode(val)}
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  label={translate("accounts.fields.pricingPlan")}
                  name={["pricingPlanId"]}
                >
                  <Select
                    {...pricingPlanSelectProps}
                    placeholder={translate("accounts.fields.pricingPlanPlaceholder")}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  label={translate("accounts.fields.status")}
                  name={["status"]}
                  rules={[{ required: true }]}
                >
                  <Select
                    options={[
                      {
                        label: translate("accounts.fields.statusValues.active"),
                        value: "active",
                      },
                      {
                        label: translate("accounts.fields.statusValues.inactive"),
                        value: "inactive",
                      },
                      {
                        label: translate("accounts.fields.statusValues.suspended"),
                        value: "suspended",
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
              {billingMode === "prepaid" && (
                <Col xs={12} sm={6}>
                  <Form.Item
                    label={translate("accounts.fields.credit")}
                    name={["credit"]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              )}
            </Row>
            <Row gutter={[16, 0]} align="middle">
              <Col xs={8} sm={4}>
                <Form.Item
                  label={translate("accounts.fields.applyVat")}
                  name={["applyVat"]}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col xs={8} sm={6}>
                <Form.Item
                  label={translate("accounts.fields.customVatRate")}
                  name={["customVatRate"]}
                >
                  <InputNumber
                    min={0}
                    max={100}
                    precision={2}
                    addonAfter="%"
                    className="w-full"
                  />
                </Form.Item>
              </Col>
              <Col xs={8} sm={6}>
                <Form.Item label=" ">
                  <Button
                    icon={<KeyOutlined />}
                    onClick={() => setIsApiKeyModalOpen(true)}
                  >
                    {translate("accounts.fields.bFlashApiKey")}
                  </Button>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name={["bFlashApiKey"]} hidden>
              <Input />
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      <Modal
        title={translate("accounts.fields.bFlashApiKey")}
        open={isApiKeyModalOpen}
        onCancel={() => setIsApiKeyModalOpen(false)}
        footer={null}
        destroyOnClose
      >
        <Form.Item>
          <Input.TextArea
            rows={4}
            value={bFlashApiKey}
            onChange={(e) => form.setFieldValue("bFlashApiKey", e.target.value)}
          />
        </Form.Item>
      </Modal>
    </>
  );
};
